import { sidebarChatIdAtomFamily } from '@/atoms/chatSidebarAtoms';
import { useQuerySessionChats } from '@/hooks/query/useQuerySessionChats';
import { isWorkHarnessChat } from '@heidi/agents/contracts';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';

/**
 * Hook that auto-selects the most recently updated chat when switching to a session.
 *
 * When a session is loaded and no chat is currently selected (sidebarChatId is null),
 * this hook will automatically select the chat with the most recent `updated_at` timestamp.
 *
 * IMPORTANT: Auto-select only happens ONCE per session (on initial load).
 * If user clicks "New Chat" (which sets sidebarChatId to null), we don't auto-select again.
 * This is tracked via a ref that resets when sessionId changes.
 *
 * @param sessionId - The current session ID
 * @param enabled - False to never auto-select (Work in session opens on Work home)
 * @returns The current sidebarChatId (auto-selected or user-selected)
 */
export function useAutoSelectRecentSessionChat(
  sessionId: string | undefined,
  enabled = true
) {
  const [sidebarChatId, setSidebarChatId] = useAtom(
    sidebarChatIdAtomFamily(sessionId ?? '')
  );
  const { data: chatsData, isLoading } = useQuerySessionChats(sessionId);

  // Track whether we've already done the initial auto-select for this session
  // This prevents re-selecting when user clicks "New Chat"
  const hasAutoSelectedRef = useRef(false);
  const lastSessionIdRef = useRef(sessionId);

  // Reset the auto-select flag when session changes
  if (sessionId !== lastSessionIdRef.current) {
    hasAutoSelectedRef.current = false;
    lastSessionIdRef.current = sessionId;
  }

  // If sidebarChatId was set externally (e.g., by handleMetadata when a chat
  // is auto-created during protocol streaming), mark as "already selected" so
  // the auto-select effect doesn't override the user's subsequent "New chat"
  // action by re-selecting this chat.
  if (sidebarChatId !== null) {
    hasAutoSelectedRef.current = true;
  }

  // Evidence cannot render harness (v2) chats — skip them even when Work is on.
  const mostRecentChat = useMemo(() => {
    const evidenceChats = (chatsData?.chats ?? []).filter(
      (chat) => !isWorkHarnessChat(chat)
    );
    if (!evidenceChats.length) return null;

    return evidenceChats.reduce((mostRecent, chat) =>
      new Date(chat.updated_at) > new Date(mostRecent.updated_at) ? chat : mostRecent
    );
  }, [chatsData?.chats]);

  // Auto-select the most recent Evidence chat when:
  // 1. Session exists
  // 2. No chat is currently selected (sidebarChatId is null)
  // 3. Chats have finished loading
  // 4. There's at least one Evidence chat
  // 5. We haven't already auto-selected for this session
  useEffect(() => {
    if (
      enabled &&
      sessionId &&
      sidebarChatId === null &&
      !isLoading &&
      mostRecentChat &&
      !hasAutoSelectedRef.current
    ) {
      hasAutoSelectedRef.current = true;
      setSidebarChatId(mostRecentChat.id);
    }
  }, [enabled, sessionId, sidebarChatId, isLoading, mostRecentChat, setSidebarChatId]);

  return sidebarChatId;
}
